import { useNavigate } from 'react-router-dom'
import React, { useState, useEffect } from 'react'
import { Search, Plus, Edit, Building2, Film } from 'lucide-react'
import { Card, Button, Badge } from '../ui'
import {
  organizationService,
  Organization,
  OrganizationListParams
} from '../../services/organizationService'
import OrganizationTabbedDrawer from './OrganizationTabbedDrawer'
import { getSubscriptionTierDisplayName } from '../../utils/subscriptionHelpers'

const OrganizationList: React.FC = () => {
  const navigate = useNavigate()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalOrganizations, setTotalOrganizations] = useState(0)
  const limit = 20

  const loadOrganizations = async (params: OrganizationListParams = {}) => {
    try {
      setLoading(true)
      setError(null)

      const response = await organizationService.getOrganizations({
        page: currentPage,
        limit,
        search: searchTerm || undefined,
        ...params
      })

      if (response.success) {
        setOrganizations(response.data.organizations)
        setTotalOrganizations(response.data.pagination.total)
        const calculatedTotalPages = Math.ceil(
          response.data.pagination.total / limit
        )
        setTotalPages(calculatedTotalPages)
      } else {
        throw new Error('Failed to load organizations')
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load organizations'
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrganizations()
  }, [currentPage])

  // Auto-search when search term changes (with debounce)
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (currentPage === 1) {
        loadOrganizations()
      } else {
        setCurrentPage(1) // This will trigger loadOrganizations via the currentPage effect
      }
    }, 300) // 300ms debounce

    return () => clearTimeout(debounceTimer)
  }, [searchTerm])

  const handleEditOrganization = (organization: Organization) => {
    setSelectedOrganization(organization)
    setIsCreating(false)
    setIsDrawerOpen(true)
  }

  const handleCreateOrganization = () => {
    setSelectedOrganization(null)
    setIsCreating(true)
    setIsDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    setSelectedOrganization(null)
    setIsCreating(false)
  }

  const handleOrganizationSaved = () => {
    loadOrganizations()
    handleCloseDrawer()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  if (loading && organizations.length === 0) {
    return (
      <div className='space-y-6'>
        <div className='flex items-center justify-between'>
          <h2 className='text-xl font-semibold text-gray-900'>Organizations</h2>
        </div>
        <div className='flex items-center justify-center py-12'>
          <div className='text-gray-500'>Loading organizations...</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div>
            <h2 className='text-xl font-semibold text-gray-900'>
              Organizations
            </h2>
            <p className='text-sm text-gray-600'>
              Manage organizations and their subscriptions
            </p>
          </div>
          <Button onClick={handleCreateOrganization}>
            <Plus className='h-4 w-4 mr-2' />
            New Organization
          </Button>
        </div>

        {/* Search */}
        <div className='relative'>
          <Search className='h-4 w-4 absolute left-3 top-3 text-gray-400' />
          <input
            type='text'
            placeholder='Search organizations...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent'
          />
        </div>

        {/* Error State */}
        {error && (
          <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg'>
            {error}
          </div>
        )}

        {/* Organizations List */}
        <div className='space-y-4'>
          {organizations.map((organization) => (
            <Card key={organization.id} variant='outlined' className='p-6'>
              <div className='flex items-start justify-between'>
                <div className='flex items-start space-x-4 flex-1'>
                  <div className='p-2 bg-blue-100 rounded-lg'>
                    <Building2 className='h-5 w-5 text-blue-600' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center space-x-3 mb-3'>
                      <h3 className='text-lg font-medium text-gray-900 truncate'>
                        {organization.organizationName}
                      </h3>
                      <Badge
                        variant={organization.isActive ? 'success' : 'error'}
                      >
                        {organization.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className='grid grid-cols-2 md:grid-cols-5 gap-4 text-sm'>
                      <div>
                        <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Status
                        </span>
                        <p className='text-sm text-gray-900 font-medium mt-1 capitalize'>
                          {organization.subscriptionStatus}
                        </p>
                      </div>
                      <div>
                        <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Plan
                        </span>
                        <p className='text-sm text-gray-900 font-medium mt-1'>
                          {getSubscriptionTierDisplayName(
                            organization.subscriptionTier
                          )}
                        </p>
                      </div>
                      <div>
                        <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Customer ID
                        </span>
                        <p className='font-mono text-xs text-gray-700 mt-1 truncate'>
                          {organization.customerId.substring(0, 8)}...
                        </p>
                      </div>
                      <div>
                        <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Created
                        </span>
                        <p className='text-sm text-gray-900 mt-1'>
                          {formatDate(organization.createdAt)}
                        </p>
                      </div>
                      {organization.subscriptionExpiresAt && (
                        <div>
                          <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                            Expires
                          </span>
                          <p className='text-sm text-gray-900 mt-1'>
                            {formatDate(organization.subscriptionExpiresAt)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className='flex flex-col items-stretch gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => handleEditOrganization(organization)}
                  >
                    <Edit className='h-3 w-3 mr-1' />
                    Edit
                  </Button>
                  {/* Entering broadcast admin from the organization row is what
                      makes the customer unambiguous — the id travels in the URL
                      rather than in selected-customer state. */}
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() =>
                      navigate(
                        `/support/broadcast/${organization.customerId}` +
                          `?org=${encodeURIComponent(organization.organizationName || '')}`
                      )
                    }
                    title='Credits, periods and broadcast library for this organization'
                  >
                    <Film className='h-3 w-3 mr-1' />
                    Broadcast
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Pagination */}
        {organizations.length > 0 && (
          <div className='flex items-center justify-between'>
            <p className='text-sm text-gray-700'>
              Showing {(currentPage - 1) * limit + 1} to{' '}
              {Math.min(currentPage * limit, totalOrganizations)} of{' '}
              {totalOrganizations} organizations
            </p>
            <div className='flex items-center space-x-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className='text-sm text-gray-600'>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant='outline'
                size='sm'
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && organizations.length === 0 && (
          <div className='text-center py-12'>
            <Building2 className='h-12 w-12 text-gray-400 mx-auto mb-4' />
            <h3 className='text-lg font-medium text-gray-900 mb-2'>
              No organizations found
            </h3>
            <p className='text-gray-600 mb-4'>
              {searchTerm
                ? 'Try adjusting your search criteria.'
                : 'Get started by creating a new organization.'}
            </p>
            {!searchTerm && (
              <Button onClick={handleCreateOrganization}>
                <Plus className='h-4 w-4 mr-2' />
                Create Organization
              </Button>
            )}
          </div>
        )}
      </div>
      {/* Organization Drawer */}
      <OrganizationTabbedDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        organization={selectedOrganization}
        isCreating={isCreating}
        onSaved={handleOrganizationSaved}
      />
    </div>
  )
}

export default OrganizationList
